<template>
  <div>
    <div class="bg-custom-blue">
      <div class="container">
        <div class="row py-2">
          <div class="col-1">
            <select v-model="selectedYear" @change="getKPIDeviations()" class="form-select form-select-sm">
              <option v-for="(n, i) in 11" :key="'actualYear' - (5 + i)" :value="((actualYear - 5) + i)">{{ (actualYear - 5) + i }}</option>
            </select>
          </div>
          <div class="col-3 ms-auto">
            <input v-model="filterName" type="text" class="form-control form-control-sm" :placeholder='$t("general.placeholderSearch")'>
          </div>
        </div>
      </div>
    </div>

    <div v-if="filteredKPIDeviations.length" class="container my-3">
      <div class="row">
        <div class="col-12">
          <div class="h4">
            {{ $t("views.kpi.detail.headingDeviation") }}
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <div class="bg-white rounded-2 p-2 mt-4">
            <div class="row px-3">
              <div class="col-4 offset-5 text-center border-2 border-start border-end border-dark px-1 pb-3">
                {{ $t("views.kpi.detail.ytd") }}
              </div>
              <div class="col-3 text-center px-1">
                {{ $t("views.kpi.detail.yearPreview") }}
              </div>
            </div>
            <div class="row fw-bold px-3">
              <div class="col-2 px-1">
                {{ $t("views.kpi.detail.kpi") }}
              </div>
              <div class="col-2 px-1">
                {{ $t("views.kpi.edit.responsible") }}
              </div>
              <div class="col-1 px-1">
                {{ $t("views.kpi.edit.unit") }}
              </div>
              <div class="col-1 ps-3 pe-3 border-2 border-start border-dark">
                {{ $t("views.kpi.periods.period") }}
              </div>
              <div class="col-1 text-end px-1">
                {{ $t("views.kpi.edit.actual") }}
              </div>
              <div class="col-1 text-end px-0">
                <select v-model="selectedYTDPreview" class="select-th text-end">
                  <option value="actualPlan">{{ $t("views.kpi.edit.plan") }}</option>
                  <option value="actualObjective">{{ $t("views.kpi.edit.objective") }}</option>
                </select>
              </div>
              <div class="col-1 text-end ps-1 pe-3 border-2 border-end border-dark">
                {{ $t("views.kpi.detail.abbreviationDev") }}
              </div>
              <div class="col-1 text-end px-1">
                {{ $t("views.kpi.edit.forecast") }}
              </div>
              <div class="col-1 text-end px-0">
                <select v-model="selectedYearPreview" class="select-th text-end">
                  <option value="forecastPlan">{{ $t("views.kpi.edit.plan") }}</option>
                  <option value="forecastObjective">{{ $t("views.kpi.edit.objective") }}</option>
                </select>
              </div>
              <div class="col-1 text-end ps-1 pe-3">
                {{ $t("views.kpi.detail.abbreviationDev") }}
              </div>
            </div>
            <div v-for="deviation in filteredKPIDeviations" :key="deviation.id" class="row px-3 border-bottom">
              <div class="col-2 my-auto col-kpi-name px-1">
                <svg v-if="deviation.status_deviation === 'H'" class="bi" width="16" height="16" fill="currentColor"> <title>{{ $t("views.kpi.edit.rating.H") }}</title > <use xlink:href="@/assets/icons/icons.svg#kpi-high-values" /> </svg>
                <svg v-else-if="deviation.status_deviation === 'L'" class="bi" width="16" height="16" fill="currentColor"> <title>{{ $t("views.kpi.edit.rating.L") }}</title > <use xlink:href="@/assets/icons/icons.svg#kpi-low-values" /> </svg>
                <svg v-else class="bi" width="16" height="16" fill="currentColor"> <title>{{ $t("views.kpi.edit.rating.N") }}</title > <use xlink:href="@/assets/icons/icons.svg#kpi" /> </svg>
                &nbsp;
                <a @click="selectKPI(deviation.id)" :href="'#kpiPeriodsModal' + selectedKPI.id" data-bs-toggle="modal">{{ deviation.name }}</a>
              </div>
              <div class="col-2 my-auto px-1">
                {{ deviation.responsible }}
              </div>
              <div class="col-1 my-auto px-1">
                {{ deviation.unit }}
              </div>
              <div class="col-1 ps-3 pe-3 py-2 border-2 border-start border-dark">
                {{ formatPeriod(deviation.period) }}
              </div>
              <div class="col-1 text-end my-auto px-1">
                {{ deviation.actual }}
              </div>
              <div class="col-1 text-end my-auto px-1">
                <div v-if="selectedYTDPreview === 'actualPlan'" class="text-end">{{ deviation.plan }}</div>
                <div v-else class="text-end">{{ deviation.objective }}</div>
              </div>
              <div class="col-1 text-end border-2 border-end border-dark ps-3 pe-3 py-2">
                <div v-if="selectedYTDPreview === 'actualPlan'" class="text-end" :class="getDevColor(unFormatNumber(deviation.actual), unFormatNumber(deviation.plan), deviation.status_deviation, deviation.status_yellow)">{{ deviation.devActualPlan }}</div>
                <div v-else class="text-end" :class="getDevColor(unFormatNumber(deviation.actual), unFormatNumber(deviation.objective), deviation.status_deviation, deviation.status_yellow)">{{ deviation.devActualObjective }}</div>
              </div>
              <div class="col-1 text-end my-auto px-1">
                {{ deviation.yearForecast }}
              </div>
              <div class="col-1 text-end my-auto px-1">
                <div v-if="selectedYearPreview === 'forecastPlan'" class="text-end">{{ deviation.yearPlan }}</div>
                <div v-else class="text-end">{{ deviation.yearObjective }}</div>
              </div>
              <div class="col-1 text-end my-auto ps-1 pe-3">
                <div v-if="selectedYearPreview === 'forecastPlan'" class="text-end" :class="getDevColor(unFormatNumber(deviation.yearForecast), unFormatNumber(deviation.yearPlan), deviation.status_deviation, deviation.status_yellow)">{{ deviation.devForecastPlan }}</div>
                <div v-else class="text-end" :class="getDevColor(unFormatNumber(deviation.yearForecast), unFormatNumber(deviation.yearObjective), deviation.status_deviation, deviation.status_yellow)">{{ deviation.devForecastObjective }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="!loadingStatus" class="text-center mt-5">
      <img src="@/assets/img/no_data.svg" class="img-fluid" width="250">
      <p class="h5 mt-3">{{ $t("views.kpi.detail.noKPIs") }}.</p>
    </div>
    <KPIPeriodsModal :kpi="selectedKPI"></KPIPeriodsModal>
  </div>
</template>

<script>
import * as formatUtils from '../../composables/formatUtils'
import KPIPeriodsModal from '../../components/monitoring/KPIPeriodsModal.vue'

export default {
  name: 'MonitoringKPIDeviation',
  data () {
    return {
      selectedYear: this.$store.state.user?.settings?.favorites?.kpiDeviationSelectedYear ? this.$store.state.user.settings.favorites.kpiDeviationSelectedYear : new Date().getFullYear(),
      selectedYTDPreview: 'actualPlan',
      selectedYearPreview: 'forecastPlan',
      filterName: ''
    }
  },
  components: {
    KPIPeriodsModal
  },
  computed: {
    kpiDeviations () {
      return this.$store.state.kpiDeviations
    },
    filteredKPIDeviations () {
      return this.kpiDeviations.filter(deviation => {
        return deviation.name.toLowerCase().includes(this.filterName.toLowerCase())
      })
    },
    selectedKPI () {
      return this.$store.state.selectedKPI
    },
    loadingStatus () {
      return this.$store.state.loadingStatus
    },
    actualYear () {
      const actualYear = new Date().getFullYear()
      return actualYear
    }
  },
  methods: {
    async getKPIDeviations () {
      this.$store.commit('SET_LOADING_STATUS', true)
      await this.$store.dispatch('getKPIDeviations', this.selectedYear)
      await this.$store.dispatch('favoriteObject', { isList: false, objectKey: 'kpiDeviationSelectedYear', objectValue: this.selectedYear })
      this.$store.commit('SET_LOADING_STATUS', false)
    },
    formatPeriod (period) {
      return formatUtils.formatPeriod(period)
    },
    getDevColor (actual, plan, statusDeviation, statusYellow) {
      return formatUtils.getDevColor(actual, plan, statusDeviation, statusYellow)
    },
    async selectKPI (kpiId) {
      this.$store.commit('SET_LOADING_STATUS', true)
      await this.$store.dispatch('getKPI', { mode: 'manage', id: kpiId })
      this.$store.commit('SET_LOADING_STATUS', false)
    },
    unFormatNumber (number) {
      const userLocale = this.$store.state.user.settings?.language
      return formatUtils.unFormatNumber(userLocale, number)
    }
  },
  async created () {
    this.$store.commit('SET_LOADING_STATUS', true)
    await this.$store.dispatch('getKPIDeviations', this.selectedYear)
    await this.$store.dispatch('getKPIUnits')
    this.$store.commit('SET_LOADING_STATUS', false)
  }
}
</script>

<style scoped>
.container {
  max-width: 1600px;
}
.col-kpi-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.select-th {
  background-color: transparent;
  border: 0;
  font-weight: bold;
}
</style>
